import React, { useState } from "react";
import styles from "./invoice_supplier_section.module.css";
import {
    Checkbox,
    Content,
    Dialog,
    DialogContainer,
    Flex,
    Heading,
    Link,
    TextField,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import CustomerDroDown from "./formComponents/CustomerDropDown";

// ✅ added (same as supplier/customer)
import { usePreloadAssets } from "../../../../../hooks/UsePreloadAssets";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { GetContactRequest } from "../../../../../services/soap/main/requests/GetContactRequest";

interface IInvoiceShippingSection {
    invoice: any;
    isEdit: any;
    onChange: (data: any) => void;
}

const InvoiceShippingSection: React.FC<IInvoiceShippingSection> = ({
    invoice,
    isEdit,
    onChange,
}) => {
    const [showMorefileds, setShowMorefields] = useState<boolean>(false);

    const { addressTypes } = usePreloadAssets();
    const { store, mainService } = useDependency();

    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });
    const _tt = (key: string) => t(`invoice_common.${key}`, { ns: "invoice" });

    const handleShowMorefields = () => {
        setShowMorefields(!showMorefileds);
    };

    const normalizeAddresses = (addressesWrapper: any): any[] => {
        if (!addressesWrapper) return [];

        let raw = addressesWrapper;

        // ADDRESSES: { ADDRESS: ... }
        if (typeof raw === "object" && !Array.isArray(raw) && raw.ADDRESS) {
            raw = raw.ADDRESS;
        }

        const list = Array.isArray(raw) ? raw : [raw];
        return list.map((x) => x?.ADDRESS ?? x).filter(Boolean);
    };

    const pickShipToAddress = (entityType: "COMPANY" | "PERSON", addressesWrapper: any) => {
        const list = normalizeAddresses(addressesWrapper);
        if (!list.length) return null;

        const isPrimary = (a: any) => String(a?.isPrimary) === "1";

        // ✅ Ship-to match based on addressTypes
        const matchByShipToType = (a: any) => {
            const typeId = String(a?.type ?? "");
            const at = (addressTypes as any)?.[typeId];
            if (!at) return false;

            // shiptoAddress exists in your addressTypes
            return String(at?.shiptoAddress) === "1";
        };

        // 1) ship-to addressTypes match
        const shipTo = list.find(matchByShipToType);
        if (shipTo) return shipTo;

        // 2) primary
        const primary = list.find(isPrimary);
        if (primary) return primary;

        // 3) index 0
        return list[0];
    };

    const handleShipToChange = async (data: any) => {
        // if cleared
        if (!data) {
            onChange({
                ...invoice,
                shipTo: "",
                shipToName: "",
                shipToStreet: "",
                shipToCity: "",
                shipToZIP: "",
                shipToRegion: "",
                shipToCountry: "",
            });
            return;
        }

        const response: any = await mainService.getContact(
            new GetContactRequest(
                store.Server,
                store.SessionId,
                undefined,
                undefined,
                undefined,
                data.id
            )
        );

        if (response?.result !== "OK") return;

        const isPerson = !!response?.PERSON;
        const entity = isPerson ? response.PERSON : response.COMPANY;
        const entityType: "PERSON" | "COMPANY" = isPerson ? "PERSON" : "COMPANY";

        const address = pickShipToAddress(entityType, entity?.ADDRESSES);

        onChange({
            ...invoice,
            shipTo: data.id,
            shipToName: data?.name ?? entity?.name1 ?? entity?.name ?? "",

            shipToStreet: address?.street ?? "",
            shipToCity: address?.cityName ?? address?.city ?? "",
            shipToZIP: address?.zipName ?? address?.zip ?? "",
            shipToRegion: address?.regionName ?? address?.region ?? "",
            shipToCountry: address?.countryName ?? address?.country ?? "",
        });
    };

    function SupplierModal() {
        return (
            <Dialog
                width={{ L: "800px", base: "100%" }}
                aria-label="Add Product"
                isDismissable
                onDismiss={() => setShowMorefields(false)}
            >
                <Heading marginStart={0} level={2}>
                    {_t(isEdit ? "update_invoice" : "add_invoice")}
                </Heading>

                <Content>
                    <Flex
                        direction={{ base: "column", L: "row" }}
                        gap={"size-150"}
                        marginTop={"size-100"}
                    >
                        <Checkbox
                            marginTop={"size-150"}
                            width={"100%"}
                            defaultSelected={false}
                            isSelected={!!invoice?.shipTo}
                            onChange={(checked) => {
                                // If unchecked -> clear all shipto fields
                                if (!checked) {
                                    onChange({
                                        ...invoice,
                                        shipTo: "",
                                        shipToName: "",
                                        shipToStreet: "",
                                        shipToCity: "",
                                        shipToZIP: "",
                                        shipToRegion: "",
                                        shipToCountry: "",
                                    });
                                    return;
                                }

                                // If checked and we already have shipTo -> keep it (no extra API call)
                                onChange({
                                    ...invoice,
                                    shipTo: String(invoice?.shipTo) || "",
                                });
                            }}
                        >
                            Shipping
                        </Checkbox>

                        <TextField
                            width={"100%"}
                            label={_t("street")}
                            marginTop={"size-150"}
                            value={invoice?.shipToStreet}
                            onChange={(e: any) => onChange({ ...invoice, shipToStreet: e })}
                        />
                    </Flex>
                    <Flex
                        direction={{ base: "column", L: "row" }}
                        gap={"size-150"}
                        marginTop={"size-100"}
                    >
                        <CustomerDroDown
                            selectedCustomer={invoice?.shipTo}
                            CustomerName={invoice?.shipToName}
                            onChange={(data: any) => handleShipToChange(data)}
                            isEdit={isEdit}
                            width={"53%"}
                            label={_t("ship_to")}
                        />

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <TextField
                                width={"100%"}
                                label={_t("city")}
                                value={invoice?.shipToCity}
                                onChange={(e: any) => onChange({ ...invoice, shipToCity: e })}
                            />
                            <TextField
                                width={"100%"}
                                label={_t("zip_code")}
                                value={invoice?.shipToZIP}
                                onChange={(e: any) => onChange({ ...invoice, shipToZIP: e })}
                            />
                        </Flex>
                    </Flex>

                    <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                        <TextField
                            width={{ base: "100%", L: "52%" }}
                            label={_t("name")}
                            value={invoice?.shipToName}
                            onChange={(e: any) => onChange({ ...invoice, shipToName: e })}
                        />

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                            <TextField
                                width={{ base: "100%", L: "60" }}
                                label={_t("region")}
                                value={invoice?.shipToRegion}
                                onChange={(e: any) => onChange({ ...invoice, shipToRegion: e })}
                            />
                            <TextField
                                width={{ base: "100%", L: "40" }}
                                label={_t("country")}
                                value={invoice?.shipToCountry}
                                onChange={(e: any) =>
                                    onChange({ ...invoice, shipToCountry: e })
                                }
                            />
                        </Flex>
                    </Flex>
                </Content>
            </Dialog>
        );
    }

    return (
        <Flex
            direction={{ base: "column", L: "column" }}
            UNSAFE_className={styles.main_container}
        >
            <Flex justifyContent={"end"}>
                <Link
                    UNSAFE_className={styles.show_more_button}
                    onPress={handleShowMorefields}
                    isQuiet
                >
                    Show more
                </Link>
            </Flex>

            <Flex>
                <TextField
                    label={_t("ship_to")}
                    width={"100%"}
                    value={invoice?.shipToName}
                    onChange={(e: any) => onChange({ ...invoice, shipToName: e })}
                />
            </Flex>

            <DialogContainer onDismiss={() => setShowMorefields(false)}>
                {showMorefileds && SupplierModal()}
            </DialogContainer>
        </Flex>
    );
};

export default InvoiceShippingSection;
